import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Card from '../../../ui/Card';
import Button from '../../../ui/Button';
import Badge from '../../../ui/Badge';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table';
import { CellText } from '../../../ui/DataTable/cells';
import RequirePermission from '../../../components/RequirePermission';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../contexts/ModalContext';
import { importLogService, IMPORT_LOG_ON } from '../../../services/importLogService';
import { importUndoService } from '../../../services/importUndoService';
import { undoCounts } from '../../../services/importers/importUndo';
import { adminService } from '../../../services/adminService';
import {
    previewImport, applyImport, writesOf, fileSlotsOf, undoKindOf, storeUndoEntry, runStoreUndo
} from '../../../services/importRunner';
import logger from '../../../utils/logger';
import { dubaiDateTime } from '../../../utils/dubaiTime';

// Labels and Badge variants for the plan counters (keys come from the importer's plan.counts)
const COUNT_META = {
    add: { label: 'Add', tone: 'success' },
    update: { label: 'Update', tone: 'info' },
    skip: { label: 'Skip (unchanged)', tone: 'neutral' },
    invalid: { label: 'No id', tone: 'danger' },
    remove: { label: 'Remove', tone: 'danger' },
    notFound: { label: 'Not in file', tone: 'neutral' },
    // Counts of an "undo" entry in the history
    restored: { label: 'Restored', tone: 'success' },
    deleted: { label: 'Deleted (added by the import)', tone: 'danger' },
    keptChanged: { label: 'Kept (changed after the import)', tone: 'warning' }
};

// Importers (or plan sections) may override a counter label (e.g. "No staff no." instead of "No id")
const countLabel = (key, labels) => labels?.[key] || COUNT_META[key]?.label || key;
const countTone = (key) => COUNT_META[key]?.tone || 'neutral';

/**
 * `importer.templates`: [{ label, fileName, sheet, rows }] -> an .xlsx with the
 * expected headers. `rows` is an array (a sample row) or an async function
 * returning the rows (e.g. the current fleet models with their stored values,
 * so the user edits real data instead of typing it).
 */
const downloadTemplate = async (t) => {
    const rows = typeof t.rows === 'function' ? await t.rows() : (t.rows || []);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t.sheet || 'Template');
    XLSX.writeFile(wb, t.fileName || 'template.xlsx');
};

export const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? String(iso) : dubaiDateTime(d, { seconds: true });
};

/** { docs, creates, restores } of an undo entry: the store's last import (storeUndoEntry) or a Firestore snapshot. */
const entryCounts = (entry) => (entry?.store ? { docs: entry.docs, creates: entry.creates, restores: entry.restores } : undoCounts(entry));

export const fmtCounts = (counts, labels) => Object.entries(counts || {})
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => `${countLabel(k, labels)}: ${v}`)
    .join(', ');

function SampleTable({ rows }) {
    if (!rows || rows.length === 0) return null;
    const columns = Object.keys(rows[0]).slice(0, 6);
    return (
        <TableContainer maxHeight={360}>
            <Table sticky>
                <TableHead>
                    <TableRow>
                        {columns.map(c => <TableHeader key={c} className="whitespace-nowrap">{c}</TableHeader>)}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={i}>
                            {columns.map(c => <TableCell key={c}><CellText>{String(r[c] ?? '')}</CellText></TableCell>)}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/** Counters + sample tables of one plan (or one section of a combined plan). */
function PlanSummary({ id, counts, samples, total, labels, openSample, onToggleSample }) {
    const sampleKeys = Object.keys(samples || {}).filter(k => (samples[k] || []).length > 0);
    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-2">
                {Object.entries(counts || {}).map(([k, v]) => (
                    <Badge key={k} variant={countTone(k)}>
                        <span className="font-semibold">{countLabel(k, labels)}</span>: {Number(v).toLocaleString()}
                    </Badge>
                ))}
                {typeof total === 'number' && (
                    <Badge variant="neutral">
                        <span className="font-semibold">Rows in file</span>: {total.toLocaleString()}
                    </Badge>
                )}
            </div>
            {sampleKeys.length > 0 && (
                <div>
                    <div className="flex flex-wrap gap-1 mb-2">
                        {sampleKeys.map(k => {
                            const key = `${id}:${k}`;
                            return (
                                <Button key={k} size="xs" variant={openSample === key ? 'primary' : 'secondary'}
                                    aria-pressed={openSample === key} onClick={() => onToggleSample(key)}>
                                    {countLabel(k, labels)} samples ({samples[k].length})
                                </Button>
                            );
                        })}
                    </div>
                    {openSample && openSample.startsWith(`${id}:`) && <SampleTable rows={samples[openSample.slice(id.length + 1)]} />}
                </div>
            )}
        </div>
    );
}

/**
 * Generic importer card: one file picker per declared file, mode selector,
 * Preview -> counts + samples (per section for combined plans), Apply with
 * progress bar, and the last import_log entry of the table. Optional plan
 * fields: `warning` (red), `review` [{ label, detail }] (amber list of rows
 * that need a human look) and `notes` [string] (parser remarks).
 */
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;
/** Max rows of `plan.review` (records the importer could only partly read) listed in the preview. */
const REVIEW_LIMIT = 20;

/** True when the table was never imported or the last import is older than 24 h. */
export const isStale = (lastLog) => {
    const at = lastLog?.at ? new Date(lastLog.at).getTime() : NaN;
    return !(at > 0) || Date.now() - at > STALE_AFTER_MS;
};

export const ageText = (iso) => {
    const at = iso ? new Date(iso).getTime() : NaN;
    if (!(at > 0)) return 'never';
    const h = Math.floor((Date.now() - at) / 3600000);
    if (h < 1) return 'less than an hour ago';
    if (h < 48) return `${h} h ago`;
    return `${Math.floor(h / 24)} days ago`;
};

export const FreshnessBadge = ({ lastLog }) => (isStale(lastLog)
    ? <Badge variant="danger" size="sm" dot title="No import in the last 24 hours">Needs update</Badge>
    : <Badge variant="success" size="sm" dot>Up to date</Badge>);

/**
 * `onLogChange(log)` is called with the latest import_log entry on mount and after every apply / check.
 * `autoUndo` opens the undo confirmation as soon as a usable snapshot is loaded
 * (the hub card links here with `?undo=1`).
 */
export default function ImportCard({ importer, onLogChange, autoUndo = false }) {
    const { currentUser } = useAuth();
    const { showToast } = useModal();
    const inputRefs = useRef({});

    const slots = fileSlotsOf(importer);

    const [filesById, setFilesById] = useState({});
    const [mode, setMode] = useState(importer.modes[0]?.id);
    const [plan, setPlan] = useState(null);
    const [phase, setPhase] = useState('idle'); // idle | previewing | ready | applying | done
    const [progress, setProgress] = useState(null); // { done, total, label }
    const [notice, setNotice] = useState(null); // { tone: ok | warn | err, text }
    const [lastLog, setLastLog] = useState(null);
    const [openSample, setOpenSample] = useState(null);
    const [confirmText, setConfirmText] = useState(null); // in-card confirmation before a destructive apply
    const [undoEntry, setUndoEntry] = useState(null);     // import_undo doc (or the store's last import) when it can be undone
    const [undoPhase, setUndoPhase] = useState(null);     // null | confirm | running
    const confirmResolve = useRef(null);                  // resolver of the in-card confirmation promise

    // a Firestore snapshot, or the store's own undo (the staff import under USE_CONTRACTS_V2)
    const undoable = undoKindOf(importer) !== null;
    const busy = phase === 'previewing' || phase === 'applying' || undoPhase === 'running';
    const chosen = slots.filter(s => filesById[s.id]);
    const canPreview = chosen.length > 0 && slots.every(s => !s.required || filesById[s.id]);

    const refreshLog = useCallback(async () => {
        const log = await importLogService.getLatest(importer.table);
        setLastLog(log);
        if (typeof onLogChange === 'function') onLogChange(log);
        // One extra read, only for tables that can be undone and only when the log names its entry; the store's
        // undo is this session's last import (no read)
        const kind = undoKindOf(importer);
        setUndoEntry(kind === 'store' ? storeUndoEntry(importer)
            : kind === 'snapshot' ? await importUndoService.getReadyEntryFor(importer.table, log) : null);
    }, [importer, onLogChange]);

    useEffect(() => { refreshLog(); }, [refreshLog]);

    useEffect(() => { if (autoUndo && undoEntry) setUndoPhase('confirm'); }, [autoUndo, undoEntry]);

    const reset = () => { setPlan(null); setProgress(null); setNotice(null); setOpenSample(null); setConfirmText(null); setPhase('idle'); };

    const onFile = (slotId) => (e) => {
        const f = e.target.files && e.target.files[0];
        setFilesById(prev => ({ ...prev, [slotId]: f || null }));
        reset();
    };

    const onMode = (id) => { setMode(id); setPlan(null); setNotice(null); setProgress(null); setConfirmText(null); setPhase('idle'); };

    const preview = async () => {
        if (!canPreview) return;
        setPhase('previewing'); setNotice(null); setPlan(null); setProgress(null);
        try {
            const { plan: p, rowCount, writes, recorded, recordError } = await previewImport({ importer, mode, filesById });
            setPlan(p);
            if (writes > 0) {
                setPhase('ready');
                setNotice({ tone: 'warn', text: `${rowCount.toLocaleString()} rows. Review the preview, then press Apply to write ${writes.toLocaleString()} change(s) to "${importer.table}".` });
            } else if (recorded) {
                // Nothing to write = already updated, and the check was recorded right away
                setPhase('done');
                setNotice({ tone: 'ok', text: `Checked just now - already up to date. ${rowCount.toLocaleString()} rows, nothing to write to "${importer.table}".` });
                refreshLog();
            } else {
                setPhase('ready');
                setNotice({ tone: 'warn', text: `Nothing to write: ${rowCount.toLocaleString()} rows, everything is already up to date - but the check could not be recorded (${recordError?.code || recordError?.message}). Press "Mark as up to date" to retry.` });
            }
        } catch (err) {
            logger.error(`[Database] preview failed (${importer.id}):`, err);
            setPhase('idle');
            setNotice({ tone: 'err', text: `Preview failed: ${err.message || err}` });
        }
    };

    /**
     * Apply gate handed to the runner: a "danger" mode or a plan that removes /
     * archives existing data (the staff replace) shows the in-card
     * confirmation with its own Yes / Cancel buttons and resolves with its
     * answer. The browser confirm dialog is not used: some shells (desktop
     * wrappers, embedded web views) return false from it without showing
     * anything, which made Apply look dead.
     */
    const askConfirm = (question) => new Promise(resolve => {
        setConfirmText(question);
        confirmResolve.current = (ok) => { confirmResolve.current = null; setConfirmText(null); resolve(ok); };
    });

    const apply = async () => {
        if (!plan) return;
        const writes = writesOf(plan);
        try {
            const res = await applyImport({
                importer, mode, filesById, plan, currentUser,
                confirm: askConfirm,
                onStart: () => { setPhase('applying'); setNotice(null); },
                onProgress: (p) => setProgress(p)
            });
            if (res.cancelled) return; // the confirmation was declined: stay on the preview
            setPhase('done');
            if (writes === 0) setProgress(null);
            const undoText = res.snapshotError
                ? ` The undo snapshot could not be saved (${res.snapshotError.code || res.snapshotError.message}), so this import cannot be undone.`
                : '';
            const doneText = writes > 0
                ? `Applied to "${importer.table}": ${fmtCounts(res.counts, importer.countLabels)}.`
                : `"${importer.table}" confirmed up to date (nothing needed writing).`;
            setNotice(res.logError
                ? { tone: 'err', text: `${doneText} But the import log could not be saved (${res.logError.code || res.logError.message}); the "last update" badge will not change.${undoText}` }
                : { tone: res.snapshotError ? 'warn' : 'ok', text: `${doneText}${undoText}` });
            if (showToast) showToast(`${importer.title}: import applied`, 'success');
            refreshLog();
        } catch (err) {
            logger.error(`[Database] apply failed (${importer.id}):`, err);
            setPhase('ready');
            // partialApplyError already says whether part of the file was written and whether
            // "Undo last import" can take it back (ADM-05: "nothing else was written" was not true
            // for multi-step / batched applies)
            setNotice({ tone: 'err', text: `Apply failed: ${err.message || err}` });
            if (err && err.partial) refreshLog();
        }
    };

    /**
     * Restores every document the last import changed (full set / delete), then logs an `undo` entry: the Firestore
     * snapshot through importUndoService, the store's own undo through the importer (runStoreUndo).
     */
    const runUndo = async () => {
        if (!undoEntry) return;
        const counts = entryCounts(undoEntry);
        setUndoPhase('running'); setNotice(null); setConfirmText(null);
        setProgress({ done: 0, total: counts.docs || 1, label: 'Undoing the last import...' });
        try {
            const res = undoEntry.store
                ? await runStoreUndo(importer, (p) => setProgress(p))
                : await importUndoService.undo(undoEntry.id, (p) => setProgress(p));
            if (!res || res.success === false) throw new Error(res?.error || 'Undo failed.');
            adminService.logUserAction('IMPORT_UNDO', `Database undo: ${importer.title} (${undoEntry.mode || '-'})`, currentUser,
                { table: importer.table, entryId: undoEntry.id || null, restored: res.restored, deleted: res.deleted, keptChanged: res.skipped || 0 });
            setPlan(null); setPhase('idle');
            const kept = res.skippedDocs || [];
            const keptText = kept.length > 0
                ? ` ${kept.length.toLocaleString()} document(s) changed after the import were kept as they are: ${kept.slice(0, 10).map(k => k.id).join(', ')}${kept.length > 10 ? ', ...' : ''}.`
                : '';
            const logText = res.logError
                ? ` But the import log could not be saved (${res.logError.code || res.logError.message}); the "last update" badge will not change.`
                : '';
            setNotice({ tone: kept.length > 0 || res.logError ? 'warn' : 'ok', text: `Undone: ${res.restored.toLocaleString()} document(s) restored and ${res.deleted.toLocaleString()} document(s) added by the import deleted in "${importer.table}".${keptText}${logText}` });
            if (showToast) showToast(`${importer.title}: last import undone`, 'success');
            setUndoPhase(null);
            refreshLog();
        } catch (err) {
            logger.error(`[Database] undo failed (${importer.id}):`, err);
            setUndoPhase(null);
            setNotice({ tone: 'err', text: `Undo failed: ${err.message || err}` });
        }
    };

    const pct = progress && progress.total ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0;
    const toggleSample = (key) => setOpenSample(openSample === key ? null : key);
    const permissionProps = importer.permissions
        ? { permissions: importer.permissions, requireAll: importer.requireAll !== false }
        : { permission: importer.permission };
    const permissionText = (importer.permissions || [importer.permission]).map(p => `"${p}"`).join(' and ');

    // Several file slots: each one sits in its own small card
    const SlotBox = slots.length > 1 ? Card : 'div';

    return (
        <Card className="h-full">
            <div className="flex items-start gap-3 mb-3">
                <Icon glyph={ICONS.upload} size="lg" tone="primary" />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-gray-900 m-0">{importer.title}</h3>
                        {IMPORT_LOG_ON && <FreshnessBadge lastLog={lastLog} />}
                    </div>
                    <p className="text-xs text-gray-500 m-0">Table: <code>{importer.table}</code></p>
                    {IMPORT_LOG_ON && <p className={`text-xs m-0 mt-1 font-medium ${isStale(lastLog) ? 'text-red-600' : 'text-green-700'}`}>
                        Last update: {lastLog ? `${fmtDate(lastLog.at)}${lastLog.user ? ` by ${lastLog.user}` : ''} (${ageText(lastLog.at)})` : 'never'}
                    </p>}
                </div>
            </div>
            <p className="text-sm text-gray-700 mb-1">{importer.description}</p>
            {importer.hint && <p className="text-xs text-gray-500 mb-3">{importer.hint}</p>}
            {Array.isArray(importer.templates) && importer.templates.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {importer.templates.map(t => (
                        <Button key={t.fileName} variant="secondary" size="sm" leadingIcon={<Icon glyph={ICONS.download} size="sm" />} onClick={() => downloadTemplate(t)}>
                            {t.label}
                        </Button>
                    ))}
                </div>
            )}

            <RequirePermission {...permissionProps} fallback={<p className="text-sm font-semibold text-red-600">You do not have the {permissionText} permission.</p>}>
                {importer.modes.length > 1 && (
                    <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Import mode">
                        {importer.modes.map(m => (
                            <Button key={m.id} size="sm" onClick={() => onMode(m.id)} disabled={busy} aria-pressed={mode === m.id}
                                variant={mode === m.id ? (m.danger ? 'danger' : 'primary') : 'secondary'}>
                                {m.label}
                            </Button>
                        ))}
                    </div>
                )}

                <div className={`grid gap-2 mb-3 ${slots.length > 1 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {slots.map(slot => {
                        const file = filesById[slot.id];
                        return (
                            <SlotBox key={slot.id} {...(slots.length > 1 ? { padding: 'sm' } : {})}>
                                {slots.length > 1 && (
                                    <p className="text-xs font-medium text-gray-700 m-0 mb-1">
                                        {slot.label}{slot.required ? '' : <span className="font-normal text-gray-500"> (optional)</span>}
                                    </p>
                                )}
                                <div className="flex flex-wrap items-center gap-2">
                                    <input ref={el => { inputRefs.current[slot.id] = el; }} type="file" accept={slot.accept || '.xlsx,.xls'}
                                        onChange={onFile(slot.id)} className="hidden" disabled={busy} aria-label={slot.label || 'File'} />
                                    <Button variant="secondary" leadingIcon={<Icon glyph={ICONS.upload} size="sm" />} onClick={() => inputRefs.current[slot.id]?.click()} disabled={busy}>
                                        {file ? 'Change file' : 'Choose file'}
                                    </Button>
                                    <span className="text-xs text-gray-600 truncate max-w-[220px]" title={file?.name}>{file ? file.name : 'No file selected'}</span>
                                </div>
                            </SlotBox>
                        );
                    })}
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                    <Button variant="secondary" leadingIcon={<Icon glyph={ICONS.eye} size="sm" />} onClick={preview} isLoading={phase === 'previewing'} disabled={!canPreview || busy}>
                        Preview
                    </Button>
                    <Button variant="primary" leadingIcon={<Icon glyph={ICONS.save} size="sm" />} onClick={apply} isLoading={phase === 'applying'}
                        disabled={!plan || phase !== 'ready' || confirmText !== null}>
                        {plan && writesOf(plan) === 0 ? 'Mark as up to date' : 'Apply'}
                    </Button>
                </div>

                {confirmText && phase === 'ready' && (
                    <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-red-50 text-red-800 border border-red-200" role="alertdialog" aria-label="Confirm apply">
                        <p className="font-semibold m-0 mb-1">{confirmText}</p>
                        <p className="m-0 mb-2">Apply anyway? This writes {writesOf(plan).toLocaleString()} change(s) to "{importer.table}".</p>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button size="sm" variant="secondary" onClick={() => confirmResolve.current && confirmResolve.current(false)}>Cancel</Button>
                            <Button size="sm" variant="danger" onClick={() => confirmResolve.current && confirmResolve.current(true)}>Yes, apply now</Button>
                        </div>
                    </div>
                )}

                {notice && (
                    <div role={notice.tone === 'err' ? 'alert' : 'status'}
                        className={`mb-3 px-3 py-2 rounded-lg border text-xs ${notice.tone === 'ok' ? 'bg-green-50 text-green-800 border-green-200' : notice.tone === 'warn' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {notice.text}
                    </div>
                )}

                {progress && (
                    <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span className="truncate">{progress.label}</span>
                            <span className="tabular-nums">{pct}%</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" role="progressbar"
                            aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={progress.label}>
                            <div className="h-2 transition-all" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
                        </div>
                    </div>
                )}

                {plan && plan.warning && phase !== 'done' && (
                    <div className="mb-3 px-3 py-2 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200" role="alert">
                        {plan.warning}
                    </div>
                )}

                {plan && Array.isArray(plan.review) && plan.review.length > 0 && phase !== 'done' && (
                    <div className="mb-3 px-3 py-2 rounded-lg text-xs bg-amber-50 text-amber-800 border border-amber-200">
                        <p className="font-semibold m-0 mb-1">{plan.review.length} record(s) need review before applying (partly read from the file):</p>
                        <ul className="m-0 pl-4 list-disc">
                            {plan.review.slice(0, REVIEW_LIMIT).map((r, i) => (
                                <li key={i}><span className="font-semibold">{r.label}</span>{r.detail ? `: ${r.detail}` : ''}</li>
                            ))}
                            {plan.review.length > REVIEW_LIMIT && <li>... and {plan.review.length - REVIEW_LIMIT} more</li>}
                        </ul>
                    </div>
                )}

                {plan && Array.isArray(plan.notes) && plan.notes.length > 0 && phase !== 'done' && (
                    <ul className="mb-3 pl-4 list-disc text-xs text-gray-600">
                        {plan.notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                )}

                {plan && Array.isArray(plan.sections) && (
                    <div className="mb-3 space-y-3">
                        {plan.sections.map(section => (
                            <Card key={section.id} padding="sm">
                                <p className="text-xs font-semibold text-gray-900 m-0 mb-2">
                                    {section.title}{section.table ? <span className="font-normal text-gray-500"> - <code>{section.table}</code></span> : null}
                                </p>
                                <PlanSummary id={section.id} counts={section.counts} samples={section.samples} total={section.total}
                                    labels={section.countLabels || importer.countLabels} openSample={openSample} onToggleSample={toggleSample} />
                            </Card>
                        ))}
                    </div>
                )}

                {plan && !Array.isArray(plan.sections) && (
                    <div className="mb-3">
                        <PlanSummary id="plan" counts={plan.counts} samples={plan.samples} total={plan.total}
                            labels={importer.countLabels} openSample={openSample} onToggleSample={toggleSample} />
                    </div>
                )}
            </RequirePermission>

            <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    {!IMPORT_LOG_ON ? <span className="min-w-0 flex-1" /> : lastLog ? (
                        <span className="min-w-0 flex-1">
                            Last import: <strong>{fmtDate(lastLog.at)}</strong>
                            {lastLog.user ? ` by ${lastLog.user}` : ''}
                            {lastLog.mode ? ` (${lastLog.mode})` : ''}
                            {lastLog.fileName ? ` - ${lastLog.fileName}` : ''}
                            {lastLog.counts ? ` - ${fmtCounts(lastLog.counts, importer.countLabels)}` : ''}
                        </span>
                    ) : <span className="min-w-0 flex-1">No import recorded yet.</span>}

                    {!undoable && <span className="text-gray-500 whitespace-nowrap">Undo not available for this table</span>}
                    {undoable && undoEntry && (
                        <RequirePermission {...permissionProps} fallback={null}>
                            <Button size="xs" variant="danger" onClick={() => setUndoPhase('confirm')} disabled={busy || undoPhase !== null}
                                isLoading={undoPhase === 'running'}>
                                {undoPhase === 'running' ? 'Undoing...' : 'Undo last import'}
                            </Button>
                        </RequirePermission>
                    )}
                </div>

                {undoable && undoEntry && undoPhase === 'confirm' && (
                    <RequirePermission {...permissionProps} fallback={null}>
                    <div className="mt-2 px-3 py-2 rounded-lg text-xs bg-red-50 text-red-800 border border-red-200" role="alertdialog" aria-label="Confirm undo">
                        <p className="font-semibold m-0 mb-1">Undo the last import of "{importer.table}"?</p>
                        <p className="m-0 mb-2">
                            This restores {entryCounts(undoEntry).restores.toLocaleString()} {importer.table} document(s) to how they were before{' '}
                            {fmtDate(undoEntry.at)} and deletes {entryCounts(undoEntry).creates.toLocaleString()} created one(s).
                            {' '}A document changed after the import (engine sync, an edit in the app) is kept as it is and listed.
                            {undoEntry.fileName ? ` Imported file: ${undoEntry.fileName}.` : ''}
                            {undoEntry.store ? ' This is the last import made in this browser session.' : ''}
                        </p>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button size="sm" variant="secondary" onClick={() => setUndoPhase(null)}>Cancel</Button>
                            <Button size="sm" variant="danger" onClick={runUndo}>Yes, undo now</Button>
                        </div>
                    </div>
                    </RequirePermission>
                )}
            </div>
        </Card>
    );
}
